import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, DollarSign, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';

const LMICalculator = () => {
  const [propertyPrice, setPropertyPrice] = useState(600000);
  const [deposit, setDeposit] = useState(60000);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculations
  const loanAmount = propertyPrice - deposit;
  const lvr = propertyPrice > 0 ? (loanAmount / propertyPrice) * 100 : 0;
  const requiresLMI = lvr > 80;
  const lmiAmount = requiresLMI ? propertyPrice * 0.03 : 0;
  const totalLoan = loanAmount + lmiAmount;
  const depositPercentage = propertyPrice > 0 ? (deposit / propertyPrice) * 100 : 0;
  const depositNeededFor80 = propertyPrice * 0.2;
  const additionalDepositNeeded = Math.max(0, depositNeededFor80 - deposit);

  const getLVRStatus = () => {
    if (lvr <= 80) {
      return {
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-green-500',
        icon: CheckCircle,
        message: 'No LMI Required',
        description: 'Your LVR is 80% or below. You won\'t need to pay Lenders Mortgage Insurance.'
      };
    } else if (lvr <= 90) {
      return {
        color: 'text-yellow-600',
        bg: 'bg-yellow-50',
        border: 'border-yellow-500',
        icon: AlertCircle,
        message: 'LMI Required',
        description: 'Your LVR is between 80-90%. You will need to pay Lenders Mortgage Insurance.'
      };
    } else if (lvr <= 95) {
      return {
        color: 'text-orange-600',
        bg: 'bg-orange-50',
        border: 'border-orange-500',
        icon: AlertCircle,
        message: 'Higher LMI Cost',
        description: 'Your LVR is between 90-95%. LMI will be more expensive.'
      };
    } else {
      return {
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-500',
        icon: AlertCircle,
        message: 'Very High LMI',
        description: 'Your LVR is above 95%. LMI costs will be very high and lending options limited.'
      };
    }
  };

  const status = getLVRStatus();
  const StatusIcon = status.icon;

  return (
    <div>
      <Header />
      <div className="min-h-screen bg-page py-12">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              LMI Calculator
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Calculate your Lenders Mortgage Insurance (3% of property price when LVR exceeds 80%)
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Input Section */}
            <Card>
              <CardHeader>
                <CardTitle>Property & Deposit Details</CardTitle>
                <CardDescription>Enter your property purchase information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Property Purchase Price ($)</Label>
                  <Input 
                    type="number"
                    value={propertyPrice || ''}
                    onChange={(e) => setPropertyPrice(parseFloat(e.target.value) || 0)}
                    className="text-lg"
                  />
                </div>
                <div>
                  <Label>Your Deposit ($)</Label>
                  <Input 
                    type="number"
                    value={deposit || ''}
                    onChange={(e) => setDeposit(parseFloat(e.target.value) || 0)}
                    className="text-lg"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {depositPercentage.toFixed(1)}% of property price
                  </p>
                </div>
                
                {requiresLMI && additionalDepositNeeded > 0 && (
                  <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                    <p className="text-sm font-semibold text-blue-900 mb-1">
                      To avoid LMI:
                    </p>
                    <p className="text-sm text-blue-800">
                      You need an additional <strong>{formatCurrency(additionalDepositNeeded)}</strong> to reach 20% deposit
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results Section */}
            <Card className={`border-4 ${status.border} ${status.bg}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <StatusIcon className={`h-6 w-6 ${status.color}`} />
                  {status.message}
                </CardTitle>
                <CardDescription>{status.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center py-4">
                  <div className="text-sm text-gray-600 mb-2">Your LVR</div>
                  <div className={`text-5xl font-bold mb-2 ${status.color}`}>
                    {lvr.toFixed(2)}%
                  </div>
                  <div className="text-sm text-gray-600">
                    Loan-to-Value Ratio
                  </div>
                </div>

                {requiresLMI ? (
                  <div className="bg-white p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="h-5 w-5 text-orange-600" />
                      <span className="font-semibold text-gray-900">LMI Calculation</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Property Price:</span>
                        <span className="font-semibold">{formatCurrency(propertyPrice)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">LMI Rate:</span>
                        <span className="font-semibold">3%</span>
                      </div>
                      <div className="h-px bg-gray-200 my-2"></div>
                      <div className="flex justify-between">
                        <span className="font-bold text-gray-900">LMI Amount:</span>
                        <span className="text-2xl font-bold text-orange-600">
                          {formatCurrency(lmiAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-4 rounded-lg border-2 border-green-500">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-900">No LMI Payable</span>
                    </div>
                    <p className="text-sm text-gray-700">
                      Your deposit is 20% or more, so you don't need to pay Lenders Mortgage Insurance.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detailed Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Loan Breakdown</CardTitle>
              <CardDescription>Complete overview of your loan structure</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-700 flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-blue-500" />
                      Property Price
                    </span>
                    <span className="font-semibold text-lg">{formatCurrency(propertyPrice)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-700">Your Deposit</span>
                    <span className="font-semibold text-lg text-green-600">{formatCurrency(deposit)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-700">Deposit Percentage</span>
                    <span className="font-semibold text-lg">{depositPercentage.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-700">Loan Amount</span>
                    <span className="font-semibold text-lg">{formatCurrency(loanAmount)}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-700 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-purple-500" />
                      LVR (Loan-to-Value Ratio)
                    </span>
                    <span className={`font-semibold text-lg ${status.color}`}>
                      {lvr.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-700 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-orange-500" />
                      LMI Cost
                    </span>
                    <span className={`font-semibold text-lg ${requiresLMI ? 'text-orange-600' : 'text-green-600'}`}>
                      {requiresLMI ? formatCurrency(lmiAmount) : '$0'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-700">Total Loan (inc. LMI)</span>
                    <span className="font-semibold text-lg">{formatCurrency(totalLoan)}</span>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-900">Total to Borrow:</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {formatCurrency(totalLoan)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Information Cards */}
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-600" />
                  What is LMI?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-700">
                  <strong>Lenders Mortgage Insurance (LMI)</strong> is a one-off, non-refundable premium that protects the lender if you default on your loan.
                </p>
                <p className="text-sm text-gray-700">
                  LMI is typically required when you borrow more than 80% of the property value (LVR > 80%).
                </p>
                <p className="text-sm text-gray-700">
                  This calculator uses a rate of <strong>3% of the property price</strong> when LMI is required.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  Payment Options
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-700">
                  <strong>Option 1: Pay Upfront</strong><br/>
                  Pay the LMI amount at settlement. This doesn't increase your loan amount.
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Option 2: Add to Loan</strong><br/>
                  Capitalize the LMI cost into your loan. You'll pay interest on this amount over the loan term.
                </p>
                <p className="text-sm text-gray-700">
                  Most borrowers add LMI to their loan to preserve cash flow.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* LVR Guide */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>LVR & LMI Guide</CardTitle>
              <CardDescription>Understanding different LVR levels and LMI requirements</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-green-900 mb-1">Up to 80% LVR</div>
                    <p className="text-sm text-gray-700">
                      <strong>No LMI required.</strong> This is the ideal scenario. You have a 20% or greater deposit.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-yellow-900 mb-1">80% - 90% LVR</div>
                    <p className="text-sm text-gray-700">
                      <strong>LMI required (3% of property price).</strong> This is a common scenario. LMI makes lending less risky for the lender.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                  <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-orange-900 mb-1">90% - 95% LVR</div>
                    <p className="text-sm text-gray-700">
                      <strong>Higher LMI costs (3% of property price).</strong> Fewer lenders available. You'll need strong financials.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-red-900 mb-1">Above 95% LVR</div>
                    <p className="text-sm text-gray-700">
                      <strong>Very limited options.</strong> Very few lenders will approve loans above 95% LVR. LMI costs are very high (3% of property price).
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LMICalculator;
